import { CollectionViewer } from '@angular/cdk/collections';
import { AgrEnvironment } from '@next/core-lib/environment';
import { I18nSelectors } from '@next/core-lib/i18n';
import { AgrDataSource, PaginatedDataSource, PaginationModel } from '@next/core-lib/table-v2';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { CatalogItem, CatalogItemVM, SkuCatalogItem } from 'src/app/models/catalog-item.model';
import { isUserAllowedToOrderItem } from 'src/app/product/helpers/add-to-cart-helpers';
import { ElasticApiService } from 'src/app/services/elastic-api.service';
import { selectCatalogProductSearchString, selectCatalogSortMode, selectSelectedCatalogCategory } from 'src/app/store/selectors';

export class ProductsDataSource extends AgrDataSource<CatalogItem> implements PaginatedDataSource<CatalogItem> {
  public data$ = new BehaviorSubject<CatalogItemVM[]>([]);
  public total$ = new BehaviorSubject<number>(0);

  private pageAndSizeSubject$ = new BehaviorSubject<{ page: number; size: number }>({
    page: 0,
    size: 25,
  });
  private destroyPaginatorSubject = new Subject<void>();

  private destroySubject = new Subject<void>();
  private initialized = false;

  constructor(
    private readonly store: Store,
    private readonly elasticApiService: ElasticApiService,
    private readonly hasAtLeastAdvisorRole$: Observable<boolean>,
    private readonly env: AgrEnvironment,
  ) {
    super();
  }

  connect(_: CollectionViewer): Observable<CatalogItem[]> {
    if (!this.initialized) {
      this.store
        .select(selectSelectedCatalogCategory)
        .pipe(withLatestFrom(this.pageAndSizeSubject$))
        .subscribe(([, { size }]) => {
          this.pageAndSizeSubject$.next({ page: 0, size });
        });

      // Note that a nested combineLatest is used, because the max. number of input observables is 6 for a single combineLatest.
      combineLatest([
        combineLatest([this.hasAtLeastAdvisorRole$]),
        combineLatest([
          this.pageAndSizeSubject$,
          this.store.select(selectSelectedCatalogCategory),
          this.store.select(I18nSelectors.selectLocale),
          this.store.select(UserSelectors.selectLocation),
          this.store.select(selectCatalogProductSearchString),
          this.store.select(selectCatalogSortMode),
          this.store.select(I18nSelectors.selectLanguage),
        ]),
      ])
        .pipe(
          debounceTime(100), // needed because the location selector is emitted mutiple times
          tap(() => this.loadingSubject.next(true)),
          takeUntil(this.destroySubject),

          filter(([, [, , locale, location]]) => !!locale && !!location),
          map(([[hasAtLeastAdvisorRole], [{ page, size }, selectedSubcategory, locale, location, searchString, sortMode, language]]) => {
            if (selectedSubcategory == null) {
              // Load SKU items (Product Search)

              const salesOrganization = location.salesAreas?.[0]?.salesOrganization;
              const getSkuCatalogCount$ = this.elasticApiService.getSkuCatalogCount(searchString, language, salesOrganization);
              const getSkuCatalog$ = this.elasticApiService.getSkuCatalog({
                keywords: searchString,
                language,
                salesOrganization,
                size,
                from: page * size,
              });

              return combineLatest([getSkuCatalogCount$, getSkuCatalog$])
                .pipe(takeUntil(this.destroySubject))
                .subscribe(([skuCatalogCount, skuCatalogItems]) => {
                  this.total$.next(skuCatalogCount);
                  const catalogItemWithAvailability: CatalogItemVM[] = skuCatalogItems?.map((skuCatalogItem: SkuCatalogItem) => ({
                    sku: skuCatalogItem,
                    salesOrganization,
                    locale,
                    sortNumber: 0,
                    isMaterial: false,
                    isRoot: false,
                    description: skuCatalogItem.title,
                    entryId: skuCatalogItem.externalSkuId,
                    isUserAllowedToOrderItem: this.env.ALLOWED_ORDER_ROLES_DISABLED || isUserAllowedToOrderItem(skuCatalogItem, hasAtLeastAdvisorRole),
                  }));
                  this.loadingSubject.next(false);
                  return this.data$.next(catalogItemWithAvailability);
                });
            } else {
              // Load catalog item for selected category
              return this.elasticApiService

                .getCatalogProducts(
                  location.salesAreas,
                  locale,
                  selectedSubcategory ? selectedSubcategory.path : '/',
                  page * size,
                  size,
                  searchString,
                  sortMode,
                )
                .pipe(take(1))
                .subscribe(
                  (elasticCatalogResult) => {
                    this.total$.next(elasticCatalogResult.count);
                    const catalogItemWithAvailability: CatalogItemVM[] = elasticCatalogResult.catalogItems?.map((catalogItem: CatalogItem) => ({
                      ...catalogItem,
                      isUserAllowedToOrderItem: this.env.ALLOWED_ORDER_ROLES_DISABLED || isUserAllowedToOrderItem(catalogItem.sku, hasAtLeastAdvisorRole),
                    }));
                    this.loadingSubject.next(false);
                    return this.data$.next(catalogItemWithAvailability);
                  },
                  (_error) => this.loadingSubject.next(false),
                );
            }
          }),
        )
        .subscribe();
    }

    this.initialized = true;

    return this.data$;
  }

  connectPaginator(): Observable<PaginationModel> {
    return combineLatest([this.pageAndSizeSubject$, this.total$]).pipe(
      takeUntil(this.destroyPaginatorSubject),
      map(([{ page, size }, total]) => ({
        pageSize: size,
        pageIndex: page,
        length: total,
      })),
    );
  }

  applyPage(page: number, size: number): void {
    this.pageAndSizeSubject$.next({ page, size });
  }

  disconnect(_: CollectionViewer): void {
    if (!this.destroySubject.closed) {
      this.destroySubject.next();
      this.destroySubject.complete();
    }
  }

  disconnectPaginator(): void {
    if (!this.destroyPaginatorSubject.closed) {
      this.destroyPaginatorSubject.next();
      this.destroyPaginatorSubject.complete();
    }
  }
}
